import { createFormatForDescription as cffd } from '../createFormatForDescription'
import { Explanation, explain, explainOptional } from '../explain/Explanation'
import PaymentChannelClaimInstructions from '../types/instructions/PaymentChannelClaimInstructions'
import TransactionType from '../types/TransactionType'

/**
 * Annotate PaymentChannelClaim transactions.
 *
 * @property type - The transaction type.
 * @property amount - An explanation of the amount.
 * @property balance - An explanation of the balance.
 * @property channel - An explanation of the channel.
 * @property publicKey - An explanation of the publicKey.
 * @property signature - An explanation of the signature.
 */
export interface PaymentChannelClaimAnnotation {
  readonly type: TransactionType.PaymentChannelClaim

  readonly amount?: Explanation<string>
  readonly balance?: Explanation<string>
  readonly channel: Explanation<string>
  readonly publicKey?: Explanation<string>
  readonly signature?: Explanation<string>
}

/**
 * Takes a transaction object and returns an annotated transaction object.
 * The returned object contains explanations about the various transaction features.
 *
 * @param transactionInstructions - The input transaction.
 * @returns An annotated transaction object detailing the various transaction features.
 */
export function annotate(
  transactionInstructions: PaymentChannelClaimInstructions,
): PaymentChannelClaimAnnotation {
  const channel = explain(
    transactionInstructions.Channel,
    `The ID of the channel is ${cffd(
      transactionInstructions.Channel,
      'channel',
    )}`,
  )

  const amount = explainOptional(
    transactionInstructions.Amount,
    (theAmount) =>
      `${cffd(
        theAmount,
        'amount',
      )} XRP (in drops) can be dispensed by the channel, including XRP previously redeemed.`,
  )

  // TODO: create a better explanation of this.
  const balance = explainOptional(
    transactionInstructions.Balance,
    (theBalance) =>
      `${cffd(
        theBalance,
        'balance',
      )} XRP (in drops) delivered by this channel after processing this claim. Required to deliver XRP`,
  )

  const signature = explainOptional(
    transactionInstructions.Signature,
    (theSig) => `This claim has signature ${cffd(theSig, 'signature')}`,
  )

  const publicKey = explainOptional(
    transactionInstructions.PublicKey,
    (key) => `The signature used public key ${cffd(key, 'publicKey')}`,
  )

  return {
    type: TransactionType.PaymentChannelClaim,
    channel,
    ...(amount && { amount }),
    ...(balance && { balance }),
    ...(signature && { signature }),
    ...(publicKey && { publicKey }),
  }
}
